import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { financeApi } from "../../../finance-api-client";
import type {
	CreateAccount,
	CreateBooking,
	CreateExpense,
	CreateFiscalYear,
	UpdateAccount,
	UpdateBooking,
	UpdateExpense,
} from "../../../types";
import { queryKeys } from "../../query-keys";

// --- Accounts ---

export function useAccounts(clubId: string | null, params?: { sphere?: string; type?: string }) {
	return useQuery({
		queryKey: queryKeys.clubPortal.accounts(clubId ?? ""),
		queryFn: () => financeApi.accounts.list(clubId!, params),
		enabled: !!clubId,
	});
}

export function useCreateAccount(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateAccount) => financeApi.accounts.create(clubId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.accounts(clubId),
			}),
	});
}

export function useUpdateAccount(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ accountId, data }: { accountId: string; data: UpdateAccount }) =>
			financeApi.accounts.update(clubId, accountId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.accounts(clubId),
			}),
	});
}

export function useDeleteAccount(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (accountId: string) => financeApi.accounts.delete(clubId, accountId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.accounts(clubId),
			}),
	});
}

export function useSeedAccounts(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => financeApi.accounts.seed(clubId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.accounts(clubId),
			}),
	});
}

// --- Bookings ---

export function useBookings(
	clubId: string | null,
	params?: {
		from?: string;
		to?: string;
		sphere?: string;
		account_id?: string;
		type?: string;
	},
) {
	return useQuery({
		queryKey: queryKeys.clubPortal.bookings(clubId ?? ""),
		queryFn: () => financeApi.bookings.list(clubId!, params),
		enabled: !!clubId,
	});
}

export function useCreateBooking(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateBooking) => financeApi.bookings.create(clubId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.bookings(clubId),
			}),
	});
}

export function useUpdateBooking(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ bookingId, data }: { bookingId: string; data: UpdateBooking }) =>
			financeApi.bookings.update(clubId, bookingId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.bookings(clubId),
			}),
	});
}

export function useDeleteBooking(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (bookingId: string) => financeApi.bookings.delete(clubId, bookingId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.bookings(clubId),
			}),
	});
}

// --- Expenses ---

export function useExpenses(
	clubId: string | null,
	params?: {
		from?: string;
		to?: string;
		account_id?: string;
		vendor?: string;
	},
) {
	return useQuery({
		queryKey: queryKeys.clubPortal.expenses(clubId ?? ""),
		queryFn: () => financeApi.expenses.list(clubId!, params),
		enabled: !!clubId,
	});
}

export function useCreateExpense(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateExpense) => financeApi.expenses.create(clubId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.expenses(clubId),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.bookings(clubId),
			});
		},
	});
}

export function useUpdateExpense(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ expenseId, data }: { expenseId: string; data: UpdateExpense }) =>
			financeApi.expenses.update(clubId, expenseId, data),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.expenses(clubId),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.bookings(clubId),
			});
		},
	});
}

export function useDeleteExpense(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (expenseId: string) => financeApi.expenses.delete(clubId, expenseId),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.expenses(clubId),
			});
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.bookings(clubId),
			});
		},
	});
}

// --- Reports ---

export function useEUERReport(clubId: string | null, year?: number) {
	return useQuery({
		queryKey: queryKeys.clubPortal.euerReport(clubId ?? "", year),
		queryFn: () => financeApi.reports.euer(clubId!, year),
		enabled: !!clubId,
	});
}

export function useCashBook(clubId: string | null, from: string, to: string) {
	return useQuery({
		queryKey: queryKeys.clubPortal.cashBook(clubId ?? "", from, to),
		queryFn: () => financeApi.reports.cashBook(clubId!, from, to),
		enabled: !!clubId && !!from && !!to,
	});
}

export function useSphereSummary(clubId: string | null, year?: number) {
	return useQuery({
		queryKey: queryKeys.clubPortal.sphereSummary(clubId ?? "", year),
		queryFn: () => financeApi.reports.spheres(clubId!, year),
		enabled: !!clubId,
	});
}

// --- Fiscal Years ---

export function useFiscalYears(clubId: string | null) {
	return useQuery({
		queryKey: queryKeys.clubPortal.fiscalYears(clubId ?? ""),
		queryFn: () => financeApi.fiscalYears.list(clubId!),
		enabled: !!clubId,
	});
}

export function useCreateFiscalYear(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: CreateFiscalYear) => financeApi.fiscalYears.create(clubId, data),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.fiscalYears(clubId),
			}),
	});
}

export function useCloseFiscalYear(clubId: string) {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (yearId: string) => financeApi.fiscalYears.close(clubId, yearId),
		onSuccess: () =>
			queryClient.invalidateQueries({
				queryKey: queryKeys.clubPortal.fiscalYears(clubId),
			}),
	});
}
